import React, { Children, FC, ReactNode, useCallback } from 'react';
import { Card, CardBody } from 'reactstrap';
import { useTranslation } from '../translations';
import { classNames } from '../utils';
import { MaterialIconText } from './MaterialIconText';
import { MaterialIcon } from '.';

import './TreeView.scss';

export interface TreeElementItem {
  addText?: string;
  canDelete?: boolean;
  canEdit?: boolean;
  title: string;
  children?: TreeElementItem[];
  minChildren?: number;
}

interface TreeElementHandler {
  readonly onAddClicked: (path: number[]) => void;
  readonly onEditClicked: (path: number[]) => void;
  readonly onDeleteClicked: (path: number[]) => void;
}

interface TreeViewProps extends TreeElementHandler {
  readonly items: TreeElementItem[];
  readonly idPrefix?: string;
}

const generateTree = (
  items: TreeElementItem[],
  callbacks: TreeElementHandler,
  idPrefix = 'tree',
): React.JSX.Element[] =>
  items.map((item, idx) => {
    const { children, ...treeElementProps } = item;
    const id = `${idPrefix}-${idx}`;
    return (
      <TreeElement
        {...{ ...treeElementProps, ...callbacks }}
        id={id}
        // eslint-disable-next-line react/no-array-index-key
        key={idx}
      >
        {children && generateTree(children, callbacks, id)}
      </TreeElement>
    );
  });

const treeIdToPath = (id: string): number[] =>
  id
    .split('-')
    .slice(1)
    .map((v) => Number.parseInt(v, 10));

export const TreeView: FC<TreeViewProps> = ({ items, idPrefix, ...callbacks }) => (
  <ul className="treeview-root">{generateTree(items, callbacks, idPrefix)}</ul>
);

const TreeElement: FC<
  Omit<TreeElementHandler & TreeElementItem & { id: string }, 'children'> & {
    readonly children?: ReactNode;
  }
> = ({
  addText = 'Add New Node',
  canDelete,
  canEdit,
  children,
  minChildren,
  id,
  title,
  onAddClicked,
  onDeleteClicked,
  onEditClicked,
}) => {
  const onAddClick = useCallback(() => {
    onAddClicked(treeIdToPath(id));
  }, [id, onAddClicked]);

  const onDeleteClick = useCallback(() => {
    onDeleteClicked(treeIdToPath(id));
  }, [id, onDeleteClicked]);

  const onEditClick = useCallback(() => {
    onEditClicked(treeIdToPath(id));
  }, [id, onEditClicked]);

  const { ts } = useTranslation();

  const isParent = Array.isArray(children);

  return (
    <li className={classNames({ 'treeview-parent': isParent }, 'treeview-child mt-2')} id={id}>
      <Card>
        <CardBody className="treeview-child-header">
          {(canDelete || canEdit) && (
            <div className="treeview-child-header-icons float-end">
              {canEdit && (
                <MaterialIcon className="treeview-child-edit" name="edit" onClick={onEditClick} />
              )}
              {canDelete && (
                <MaterialIcon
                  className="treeview-child-delete"
                  name="delete"
                  onClick={onDeleteClick}
                />
              )}
            </div>
          )}
          <div>{title}</div>
        </CardBody>
      </Card>
      {isParent && (
        <ul className="treeview-parent-children mt-2">
          {minChildren && Children.count(children) < minChildren && (
            <li className="treeview-info-child">
              <MaterialIconText name="warning" type="warning">
                {ts('validation_error_this_node_requires_at_least_child_nodes', minChildren)}
              </MaterialIconText>
            </li>
          )}
          <li className="treeview-child-add treeview-info-child" onClick={onAddClick}>
            <MaterialIconText name="add">
              <div>{addText}</div>
            </MaterialIconText>
          </li>
          {children}
        </ul>
      )}
    </li>
  );
};
